import React, { cloneElement, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import sortBy from 'lodash/sortBy';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import Collapse from 'choerodon-ui/lib/collapse';
import Tag from 'choerodon-ui/lib/tag';
import { warning } from 'choerodon-ui/dataset/utils';
import CollapsePanel from 'choerodon-ui/lib/collapse/CollapsePanel';
// import { $l } from 'choerodon-ui/pro/lib/locale-context';
import BoardContext from '../BoardContext';
import ObserverSelect from '../../select';
import Form from '../../form';
import { FieldIgnore, FieldType, SortOrder } from '../../data-set/enum';
import ObserverSelectBox from '../../select-box';
import Button from '../../button';
import Icon from '../../icon';
import { ViewField, ViewMode } from '../enum';
import { getColumnKey, getHeader } from '../../table/utils';
import Tree from './tree';
import TreeNode from './tree/TreeNode';
import { treeReduce } from '../../_util/treeUtils';
import Tooltip from '../../tooltip';
import ObserverNumberField from '../../number-field';
import ObserverTextField from '../../text-field';
import { $l } from '../../locale-context';
import { ButtonColor, FuncType } from '../../button/interface';
import DataSet from '../../data-set/DataSet';
function normalizeColumnsToTreeData(columns, displayFields, displaySort) {
    return [...treeReduce(columns, (map, column, _sort, parentColumn) => {
            if ((!column.__tableGroup || (!column.children && column.__group)) && column.name) {
                const key = column.__originalKey || getColumnKey(column);
                const sort = displaySort ? displaySort.find(s => s.name === key) : undefined;
                map.set(key, {
                    key,
                    parentKey: parentColumn && (parentColumn.__originalKey || getColumnKey(parentColumn)),
                    width: column.width,
                    header: column.header,
                    title: column.title,
                    hidden: !displayFields.includes(column.name),
                    name: column.name,
                    sort: isNumber(sort) ? sort : (column.sort || 999),
                    hideable: column.hideable,
                });
            }
            return map;
        }, new Map()).values()];
}
const KanbanCustomizationSettings = function CustomizationSettings(props) {
    const { modal, viewMode } = props;
    const { onConfigChange = noop, autoQuery, customizedCode, getConfig, optionDS, prefixCls, customizedDS, dataSet, saveCustomized, displayFields } = useContext(BoardContext);
    const tempCustomized = useRef(customizedDS.current.toData());
    const defaultData = tempCustomized.current;
    const PanelRef = useRef(null);
    const customizedLoad = getConfig('customizedLoad');
    const kanbanRecord = useMemo(() => new DataSet({
        autoCreate: true,
        fields: [
            {
                name: ViewField.groupField,
                type: FieldType.string,
                options: optionDS,
                defaultValue: defaultData[ViewField.groupField],
                ignore: viewMode === ViewMode.card ? FieldIgnore.always : FieldIgnore.clean,
                required: true,
            },
            {
                name: ViewField.viewName,
                type: FieldType.string,
                defaultValue: defaultData[ViewField.viewName] || '',
                required: true,
            },
            {
                name: ViewField.viewHeight,
                type: FieldType.number,
                defaultValue: defaultData[ViewField.viewHeight] || 366,
                step: 1,
                required: true,
            },
            {
                name: ViewField.cardWidth,
                type: FieldType.number,
                defaultValue: defaultData[ViewField.cardWidth] || 6,
            },
            {
                name: ViewField.displayFields,
                type: FieldType.string,
                multiple: true,
                defaultValue: defaultData[ViewField.displayFields] || displayFields.slice(0, 3).map(field => field.name || field.header),
            },
            {
                name: ViewField.sort,
                type: FieldType.object,
                defaultValue: defaultData[ViewField.sort],
            },
            {
                name: ViewField.combineSort,
                type: FieldType.object,
                defaultValue: defaultData[ViewField.combineSort],
            },
            {
                name: ViewField.showLabel,
                type: FieldType.boolean,
                trueValue: 1,
                falseValue: 0,
                defaultValue: defaultData[ViewField.showLabel] === 0 ? 0 : 1,
            },
        ],
    }).current, []);
    const sortPrefixCls = `${prefixCls}-customization-combine-sort`;
    const { fields, props: { combineSort, }, } = dataSet;
    const sortableFieldNames = useMemo(() => displayFields.map(df => df.sortable && df.name), [displayFields]);
    const sortFieldOptions = useMemo(() => {
        const sortFieldData = [];
        if (fields && sortableFieldNames && sortableFieldNames.length > 0) {
            fields.forEach(field => {
                if (sortableFieldNames.includes(field.name)) {
                    sortFieldData.push({
                        value: field.name,
                        meaning: field.get('label') || field.name,
                    });
                }
            });
        }
        return new DataSet({
            autoQuery: false,
            autoCreate: false,
            paging: false,
            fields: [
                { name: 'value', type: FieldType.string },
                { name: 'meaning', type: FieldType.string },
            ],
            data: sortFieldData,
        });
    }, [fields, sortableFieldNames]);
    const sortDS = useMemo(() => {
        const { combineSortFieldNames = new Map() } = dataSet;
        const data = [];
        combineSortFieldNames.forEach((sortOrder, fieldName) => {
            const record = sortFieldOptions.records.find(record => record.get('value') === fieldName);
            if (record) {
                const field = dataSet.getField(fieldName);
                data.push({
                    sortName: fieldName,
                    order: sortOrder || (field && field.order) || SortOrder.asc,
                });
            }
        });
        return new DataSet({
            forceValidate: true,
            autoQuery: false,
            autoCreate: false,
            paging: false,
            fields: [
                { name: 'sortName', type: FieldType.string, options: sortFieldOptions },
                { name: 'order', type: FieldType.string, defaultValue: SortOrder.asc },
            ],
            data,
            events: {
                update: ({ dataSet }) => {
                    kanbanRecord.set('combineSort', dataSet.toData());
                },
                remove: ({ dataSet }) => {
                    kanbanRecord.set('combineSort', dataSet.toData());
                },
            },
        });
    }, [sortFieldOptions, dataSet, dataSet.combineSortFieldNames]);
    const optionsFilter = (record) => {
        return sortDS.every(sortRecord => sortRecord.get('sortName') !== record.get('value'));
    };
    const SortDragItem = ({ record, index }) => {
        const { key } = record;
        return (React.createElement(Draggable, { draggableId: String(key), index: record.index }, (pro, snapshot) => (React.createElement("span", Object.assign({ ref: pro.innerRef }, pro.draggableProps, { className: `${sortPrefixCls}-list-item${snapshot.isDragging ? ` ${sortPrefixCls}-list-item-dragging` : ''}` }),
            React.createElement("span", Object.assign({}, pro.dragHandleProps, { className: `${sortPrefixCls}-list-item-drag` }),
                React.createElement(Icon, { type: "baseline-drag_indicator" })),
            React.createElement("span", { className: `${sortPrefixCls}-list-item-index` },
                React.createElement(Tag, null, index + 1)),
            React.createElement(ObserverSelect, { placeholder: $l('Table', 'please_select_column'), className: `${sortPrefixCls}-list-item-sortName`, record: record, name: "sortName", optionsFilter: optionsFilter, notFoundContent: $l('Table', 'no_save_filter'), clearButton: false }),
            React.createElement(ObserverSelectBox, { record: record, name: "order", className: `${sortPrefixCls}-list-item-sortOrder` },
                React.createElement(ObserverSelectBox.Option, { value: SortOrder.asc }, $l('Table', 'ascending')),
                React.createElement(ObserverSelectBox.Option, { value: SortOrder.desc }, $l('Table', 'descending'))),
            React.createElement(Button, { className: `${sortPrefixCls}-list-item-delete`, icon: 'delete_black-o', funcType: FuncType.link, color: ButtonColor.primary, onClick: () => sortDS.delete(record, !!record.get('sortName')) })))));
    };
    const onDragEnd = useCallback((result) => {
        if (result.destination) {
            sortDS.move(result.source.index, result.destination.index);
            kanbanRecord.set('combineSort', sortDS.toData());
        }
    }, [sortDS.length]);
    const columnDataSet = useMemo(() => new DataSet({
        data: normalizeColumnsToTreeData(displayFields, kanbanRecord.get(ViewField.displayFields), kanbanRecord.get(ViewField.sort)),
        paging: false,
        primaryKey: 'key',
        idField: 'key',
        parentField: 'parentKey',
        events: {
            update({ dataSet, name }) {
                if (name === 'sort') {
                    kanbanRecord.set(ViewField.sort, sortBy(dataSet.records, [r => r.get('sort')]).map(r => ({ name: r.get('name'), sort: r.get('sort') })));
                }
                kanbanRecord.set(ViewField.displayFields, sortBy(dataSet.filter(r => !r.get('hidden')), [r => r.get('sort')]).map(r => r.get('name')));
            },
        },
    }), []);
    const { groupedTreeRecords } = columnDataSet;
    const handleRestoreKanban = useCallback(action((e) => {
        e.stopPropagation();
        kanbanRecord.set(defaultData);
        if (modal) {
            modal.close();
        }
    }), [kanbanRecord, modal]);
    const handleOk = useCallback(async () => {
        if (customizedDS && customizedDS.current) {
            customizedDS.current.set(kanbanRecord.toData());
            try {
                // const saveCustomized(customizedDS.current.toData());
                const customizedLoad = getConfig('customizedLoad');
                const detailRes = await saveCustomized(customizedDS.current.toData());
                const res = await customizedLoad(customizedCode, 'Board', {
                    type: 'list',
                });
                const mergeRes = res.map(r => {
                    if (r.id === detailRes[ViewField.id]) {
                        return { ...detailRes, objectVersionNumber: r.objectVersionNumber };
                    }
                    return r;
                });
                const defaultView = {
                    code: customizedCode,
                    [ViewField.viewName]: '初始列表视图',
                    [ViewField.viewMode]: ViewMode.table,
                    [ViewField.id]: '__DEFAULT__',
                };
                customizedDS.loadData([...mergeRes, defaultView]);
                const records = sortDS.filter(r => r.get('sortName') && r.get('order'));
                const sortInfo = new Map();
                records.forEach(record => {
                    sortInfo.set(record.get('sortName'), record.get('order'));
                });
                const currentViewDS = dataSet.getState('__CURRENTVIEWDS__');
                if (currentViewDS && sortInfo) {
                    currentViewDS.fields.forEach(current => {
                        current.order = undefined;
                    });
                    currentViewDS.combineSort = true;
                    currentViewDS.combineSortFieldNames = observable.map(sortInfo);
                    sortInfo.forEach((sortOrder, fieldName) => {
                        const field = currentViewDS.getField(fieldName);
                        if (field) {
                            field.order = sortOrder;
                        }
                    });
                    onConfigChange({ config: customizedDS.current.toData(), currentViewDS });
                    if (autoQuery) {
                        currentViewDS.query();
                    }
                }
            }
            catch (e) {
                // record.status = RecordStatus.sync;
            }
            if (modal) {
                modal.close();
            }
        }
    }, [kanbanRecord, modal]);
    useEffect(() => {
        if (modal) {
            modal.update({
                footer: (okBtn, cancelBtn) => (React.createElement(React.Fragment, null,
                    cloneElement(cancelBtn, { onClick: handleRestoreKanban }),
                    cloneElement(okBtn, { onClick: handleOk }))),
            });
        }
    }, [modal]);
    const loadDetail = useCallback(async () => {
        const res = await customizedLoad(customizedCode, 'Board', {
            type: 'detail',
            [ViewField.id]: defaultData[ViewField.id],
        });
        try {
            const dataJson = res.dataJson ? JSON.parse(res.dataJson) : {};
            kanbanRecord.init({ ...omit(res, 'dataJson'), ...dataJson });
            sortDS.loadData(dataJson.combineSort);
        }
        catch (error) {
            warning(false, error.message);
        }
    }, []);
    useEffect(() => {
        loadDetail();
    }, []);
    const handleDragTreeNode = useCallback(action((srcIndex, destIndex) => {
        const records = sortBy(columnDataSet.records, [r => r.get('sort')]);
        const [removed] = records.splice(srcIndex, 1);
        records.splice(destIndex, 0, removed);
        records.forEach((r, index) => {
            r.set('sort', index);
        });
    }), [columnDataSet]);
    const handleDragEnd = useCallback(({ destination, source: { index: srcIndex } }) => {
        if (destination) {
            const { 
            // droppableId: destDroppableId, 
            index: destIndex, } = destination;
            // const src = srcDroppableId.split('__--__');
            handleDragTreeNode(srcIndex, destIndex);
            // , src[1]
        }
    }, [handleDragTreeNode]);
    const renderPopupContent = useCallback(() => {
        return (React.createElement("div", { className: `${prefixCls}-customization-panel-content` },
            React.createElement(DragDropContext, { onDragEnd: handleDragEnd },
                React.createElement(Tree, null, sortBy(groupedTreeRecords[0].records, [r => r.get('sort')]).map((record, index, list) => {
                    const children = (React.createElement(TreeNode, { key: record.key, record: record, isLeaf: true, index: index, records: list }));
                    return record.get('draggable') !== false && (!record.parent || list.length > 1) ? (React.createElement(Draggable, { key: record.key, draggableId: String(record.key), index: index }, (provided, snapshot) => (cloneElement(children, { provided, snapshot })))) : children;
                })))));
    }, [kanbanRecord.get(ViewField.displayFields), kanbanRecord.get(ViewField.sort), groupedTreeRecords]);
    return (React.createElement("div", { ref: PanelRef },
        React.createElement(Collapse
        // onChange={handleCollapseChange}
        , { 
            // onChange={handleCollapseChange}
            defaultActiveKey: ['kanban', 'card', 'data'], 
            // expandIcon={renderIcon}
            expandIconPosition: "text-right", className: `${prefixCls}-customization`, ghost: true },
            React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, "\u663E\u793A\u8BBE\u7F6E"), key: "kanban" },
                React.createElement(Form, { className: `${prefixCls}-customization-form`, record: kanbanRecord },
                    React.createElement(ObserverTextField, { name: ViewField.viewName, label: "\u89C6\u56FE\u540D\u79F0" }),
                    React.createElement(ObserverSelect, { name: "groupField", label: "\u5206\u7EC4\u5B57\u6BB5", clearButton: false, hidden: viewMode !== ViewMode.kanban }),
                    React.createElement(ObserverNumberField, { label: "\u89C6\u56FE\u9AD8\u5EA6", name: ViewField.viewHeight }))),
            React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, "\u5361\u7247\u8BBE\u7F6E"), key: "card" },
                React.createElement(Form, { className: `${prefixCls}-customization-form`, record: kanbanRecord },
                    React.createElement(ObserverSelect, { name: "cardWidth", label: "\u5361\u7247\u5BBD\u5EA6", key: "cardWidth", clearButton: false },
                        React.createElement(ObserverSelect.Option, { value: 4 }, "\u7A84"),
                        React.createElement(ObserverSelect.Option, { value: 6 }, "\u9002\u4E2D"),
                        React.createElement(ObserverSelect.Option, { value: 8 }, "\u8F83\u5BBD"),
                        React.createElement(ObserverSelect.Option, { value: 12 }, "\u5BBD")),
                    React.createElement(ObserverSelect, { name: "displayFields", label: "\u663E\u793A\u5B57\u6BB5", key: "displayFields", popupContent: renderPopupContent, onClear: () => kanbanRecord.set(ViewField.displayFields, null), getPopupContainer: () => PanelRef.current }, displayFields.map(field => {
                        if (!field.hidden) {
                            const header = getHeader({
                                name: field.name,
                                title: field.title,
                                header: field.header,
                                dataSet: dataSet,
                            });
                            return React.createElement(ObserverSelect.Option, { value: field.name, key: `${field.name}_option` }, header);
                        }
                        return null;
                    }).filter(Boolean)),
                    React.createElement(ObserverSelectBox, { name: "showLabel", key: "showLabel", label: "\u663E\u793A\u5B57\u6BB5\u540D\u79F0" },
                        React.createElement(ObserverSelectBox.Option, { style: { paddingRight: 16 }, value: 1 }, "\u662F"),
                        React.createElement(ObserverSelectBox.Option, { value: 0 }, "\u5426")))),
            React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, "\u6570\u636E\u8BBE\u7F6E"), key: "data" },
                React.createElement("div", { className: `${prefixCls}-customization-panel-des` },
                    React.createElement("span", null, "\u9009\u62E9\u6B64\u89C6\u56FE\u4E0B\u7684\u8BB0\u5F55\u9ED8\u8BA4\u6392\u5E8F\u65B9\u5F0F"),
                    React.createElement(Tooltip, { title: "\u53EF\u6392\u5E8F\u7684\u524D\u63D0\u662F\u540E\u7AEF\u67E5\u8BE2\u5DF2\u652F\u6301\u6392\u5E8F" },
                        React.createElement(Icon, { type: 'help' }))),
                React.createElement("div", { className: `${sortPrefixCls}-content` },
                    React.createElement("div", { className: `${sortPrefixCls}-list-container` },
                        React.createElement(DragDropContext, { onDragEnd: onDragEnd },
                            React.createElement(Droppable, { droppableId: "combine-sort", direction: "vertical" }, (provided) => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps, { className: `${sortPrefixCls}-list` }),
                                sortDS.map((record, index) => {
                                    const { key } = record;
                                    return React.createElement(SortDragItem, { key: key, record: record, index: index });
                                }),
                                provided.placeholder))))),
                    React.createElement("div", { className: `${sortPrefixCls}-add-button` },
                        React.createElement(Button, { funcType: FuncType.link, icon: "add", onClick: () => sortDS.create(), color: ButtonColor.primary, disabled: combineSort ? sortDS.length >= sortFieldOptions.length : sortDS.length > 0 }, $l('Table', 'add_sort'))))))));
};
KanbanCustomizationSettings.displayName = 'KanbanCustomizationSettings';
export default observer(KanbanCustomizationSettings);
